import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'

// Ecrites a la main : shadcn/ui attendra d'avoir de vrais graphiques.

export function AuthCard({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-neutral-50 p-4 dark:bg-neutral-950">
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          {title}
        </h1>
        {subtitle !== undefined && (
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            {subtitle}
          </p>
        )}
        <div className="mt-6">{children}</div>
      </div>
    </main>
  )
}

export function Field({
  label,
  hint,
  ...inputProps
}: { label: string; hint?: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-neutral-800 dark:text-neutral-200">
        {label}
      </span>
      <input
        {...inputProps}
        className="mt-1.5 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100 dark:focus:border-neutral-100"
      />
      {hint !== undefined && (
        <span className="mt-1.5 block text-xs text-neutral-500 dark:text-neutral-400">
          {hint}
        </span>
      )}
    </label>
  )
}

export function Button({
  children,
  variant = 'primary',
  size = 'block',
  ...buttonProps
}: {
  variant?: 'primary' | 'ghost' | 'outline' | 'danger'
  size?: 'block' | 'sm'
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const base =
    'rounded-lg text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50'
  const dimensions = size === 'block' ? 'w-full px-4 py-2.5' : 'px-3 py-1.5'
  const skins = {
    primary:
      'bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white',
    ghost:
      'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800',
    outline:
      'border border-neutral-300 text-neutral-800 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800',
    danger:
      'border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40',
  }

  return (
    <button {...buttonProps} className={`${base} ${dimensions} ${skins[variant]}`}>
      {children}
    </button>
  )
}

export function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
      {title !== undefined && (
        <h2 className="text-lg font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          {title}
        </h2>
      )}
      <div className={title === undefined ? '' : 'mt-4'}>{children}</div>
    </section>
  )
}

export function Badge({ tone, children }: { tone: 'ok' | 'warn' | 'neutral'; children: ReactNode }) {
  const skin = {
    ok: 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900',
    warn: 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900',
    neutral:
      'bg-neutral-100 text-neutral-700 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-700',
  }[tone]

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${skin}`}>
      {children}
    </span>
  )
}

// role="alert" : sans lui, un lecteur d'ecran n'annonce pas le refus de connexion.
export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'info' | 'success'
  children: ReactNode
}) {
  const skin = {
    error:
      'border-red-200 bg-red-50 text-red-900 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200',
    info: 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/40 dark:text-blue-200',
    success:
      'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200',
  }[tone]

  return (
    <p role="alert" className={`rounded-lg border px-3 py-2 text-sm ${skin}`}>
      {children}
    </p>
  )
}
